import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_LABELS = {
  project_manager: "Project Manager", architect: "Architect", structural_engineer: "Structural Engineer",
  mep_engineer: "MEP Engineer", civil_engineer: "Civil Engineer", accountant: "Accountant",
  lender: "Lender", general_contractor: "General Contractor", consultant: "Consultant",
};
const empty = { name: "", role: "project_manager", email: "", phone: "", company: "" };

export default function TeamTab({ projectId, onChange }) {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/projects/${projectId}/team`).then(({ data }) => setMembers(data.members)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/team`, form);
      toast.success("Team member added");
      setOpen(false); setForm(empty); await load(); onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/projects/${projectId}/team/${id}`);
    toast.success("Removed"); await load(); onChange?.();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline text-primary mb-1">Phase 1 · Team Setup</div>
          <h2 className="font-heading font-bold text-xl tracking-tight">Project Team</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-team-member-button" className="rounded-none bg-primary hover:bg-primary/90 text-white gap-2">
              <Plus size={18} weight="bold" /> Add Member
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle className="font-heading font-black tracking-tight">Add Team Member</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="team-form">
              <div className="space-y-2"><Label className="overline">Name *</Label><Input value={form.name} onChange={set("name")} required data-testid="team-name-input" className="rounded-none h-11" /></div>
              <div className="space-y-2">
                <Label className="overline">Role *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="team-role-select" className="rounded-none h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label className="overline">Email *</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="team-email-input" className="rounded-none h-11" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="overline">Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="team-phone-input" className="rounded-none h-11" /></div>
                <div className="space-y-2"><Label className="overline">Company</Label><Input value={form.company} onChange={set("company")} data-testid="team-company-input" className="rounded-none h-11" /></div>
              </div>
              <Button type="submit" disabled={busy} data-testid="team-submit-button" className="w-full rounded-none h-11 bg-primary hover:bg-primary/90 text-white">
                {busy ? "Adding…" : "Add Member"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {members.length ? (
        <div className="border border-border bg-card">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Email</TableHead>
              <TableHead>Phone</TableHead><TableHead>Company</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id} className="even:bg-muted/20" data-testid={`team-row-${m.id}`}>
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell><span className="text-xs px-2 py-1 bg-secondary">{ROLE_LABELS[m.role] || m.role}</span></TableCell>
                  <TableCell className="text-muted-foreground">{m.email}</TableCell>
                  <TableCell className="text-muted-foreground">{m.phone || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{m.company || "—"}</TableCell>
                  <TableCell>
                    <button onClick={() => remove(m.id)} data-testid={`remove-team-${m.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-150"><Trash size={18} /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="bg-card border border-border border-dashed p-12 text-center">
          <UserCircle size={40} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
          <p className="text-muted-foreground">No team members yet. Add project stakeholders and assign their roles.</p>
        </div>
      )}
    </div>
  );
}
